import { ChevronRight, Plus } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardTitle } from '@/components/ui/card';
import { usePortalGetPortalMerchants } from '@/lib/api/portal/portal';
import { useHasClientRole } from '@/lib/portal-auth/usePortalAuth';
import { PortalCreateMerchantDialog } from './PortalCreateMerchantDialog';

export default function PortalMerchantsPage() {
  const [createOpen, setCreateOpen] = useState(false);
  const canCreate = useHasClientRole('ClientAdmin');
  const { data, isLoading, isError, error } = usePortalGetPortalMerchants();

  return (
    <section className="space-y-5">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Merchants</h1>
          <p className="text-sm text-muted-foreground">Your Adyen merchant accounts and the stores within each.</p>
        </div>
        {canCreate && (
          <Button onClick={() => setCreateOpen(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            New merchant
          </Button>
        )}
      </header>

      <Card>
        <CardContent className="p-0">
          {isError ? (
            <div className="p-6">
              <CardTitle className="text-destructive">Could not load merchants</CardTitle>
              <CardDescription>{(error as Error)?.message ?? 'Unknown error'}</CardDescription>
            </div>
          ) : isLoading ? (
            <div className="space-y-2 p-6">
              {Array.from({ length: 3 }, (_, i) => (
                <div key={i} className="h-12 animate-pulse rounded-md bg-muted" />
              ))}
            </div>
          ) : !data || data.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-sm text-muted-foreground">
                No merchants yet.{canCreate && ' Create the first one to route proxy calls.'}
              </p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="p-3 font-medium">Name</th>
                  <th className="p-3 font-medium">Merchant ID</th>
                  <th className="p-3 font-medium">Stores</th>
                  <th className="p-3 font-medium">Status</th>
                  <th className="w-8 p-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.map((m) => (
                  <tr key={m.id} className="hover:bg-muted/30">
                    <td className="p-3 font-medium">{m.name}</td>
                    <td className="p-3 font-mono text-xs text-muted-foreground">{m.merchantId}</td>
                    <td className="p-3 tabular-nums">{m.storeCount}</td>
                    <td className="p-3">
                      {m.isActive ? <Badge variant="success">Active</Badge> : <Badge variant="warning">Suspended</Badge>}
                    </td>
                    <td className="p-3">
                      <Link
                        to={`/portal/merchants/${m.id}`}
                        className="inline-flex items-center text-muted-foreground hover:text-foreground"
                        aria-label={`Open ${m.name}`}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {canCreate && <PortalCreateMerchantDialog open={createOpen} onOpenChange={setCreateOpen} />}
    </section>
  );
}
